import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    getJob,
    postAcceptCandidate,
    postUnassignCandidate,
    patchJob,
} from "@/api/jobs";
import { formatDateRange } from "@/lib/formatDateRange";
import { formatTimeRange } from "@/lib/formatTimeRange";
import AssignCandidateDialog from "@/pages/admin/jobs/AssignCandidateDialog.jsx";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import TimesheetsTable from "@/components/TimesheetsTable.jsx";

export default function JobShow() {
    const { id } = useParams();
    const [job, setJob] = useState(null);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [editingShiftDetails, setEditingShiftDetails] = useState(false);
    const [shiftDetailsDraft, setShiftDetailsDraft] = useState("");
    const [notesDraft, setNotesDraft] = useState("");
    const [notesTimeout, setNotesTimeout] = useState(null);

    useEffect(() => {
        async function fetchJob() {
            const data = await getJob(id);
            if (data) {
                setJob(data);
                setNotesDraft(data.notes || "");
            }
        }
        fetchJob();
    }, [id]);

    const handleAccept = async () => {
        const updatedJob = await postAcceptCandidate(job.id);
        if (updatedJob) setJob(updatedJob);
    };

    const handleUnassign = async () => {
        const updatedJob = await postUnassignCandidate(job.id);
        if (updatedJob) setJob(updatedJob);
    };

    const handleShiftDetailsSave = async () => {
        const updatedJob = await patchJob(job.id, {
            shift_details: shiftDetailsDraft,
        });
        if (updatedJob) {
            setJob(updatedJob);
            setEditingShiftDetails(false);
            toast("Shift details updated", {
                description: "Your changes have been saved.",
            });
        }
    };

    const handleNotesChange = (value) => {
        setNotesDraft(value);
        if (notesTimeout) clearTimeout(notesTimeout);
        const timeout = setTimeout(async () => {
            const updatedJob = await patchJob(job.id, { notes: value });
            if (updatedJob) {
                setJob(updatedJob);
                toast("Admin notes saved");
            }
        }, 1000);
        setNotesTimeout(timeout);
    };

    if (!job) return <div className="p-6">Loading...</div>;

    const { startDate, endDate, startTime, endTime } = job;
    const { start, end, duration } = formatTimeRange(startTime, endTime);
    const dateDisplay = formatDateRange(startDate, endDate);

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl shadow p-6 space-y-8">
                <h1 className="text-2xl font-bold text-[#5D1F42]">Job Details</h1>

                <h2 className="text-lg text-gray-800 font-semibold">
                    {job.skill?.name || "—"} at {job.venue?.name || "—"} from {" "}
                    {new Date(startDate).toLocaleDateString("en-GB", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                    })}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <h3 className="font-semibold text-[#5D1F42] mb-1">Position</h3>
                        <p>{job.skill?.name || "—"}</p>
                    </div>
                    <div>
                        <h3 className="font-semibold text-[#5D1F42] mb-1">Job Days</h3>
                        {job.jobDays && job.jobDays.length > 0 ? (
                            <div className="space-y-1">
                                {job.jobDays.map((jobDay, index) => (
                                    <p key={jobDay.id || index} className="text-sm">
                                        {new Date(jobDay.date).toLocaleDateString("en-GB", {
                                            weekday: "short",
                                            day: "numeric",
                                            month: "short",
                                            year: "numeric",
                                        })}
                                    </p>
                                ))}
                                <p className="text-blue-600 font-medium mt-2">
                                    {job.jobDays.length} day{job.jobDays.length !== 1 ? 's' : ''}
                                </p>
                            </div>
                        ) : (
                            <div>
                                <p>{dateDisplay.start}</p>
                                <p>{dateDisplay.end || "—"}</p>
                                <p className="text-blue-600">{dateDisplay.duration} days</p>
                            </div>
                        )}
                    </div>
                    <div>
                        <h3 className="font-semibold text-[#5D1F42] mb-1">Hours</h3>
                        <p>
                            {start} <span className="text-gray-400">to</span> {end}
                        </p>
                        <p className="text-blue-600">{duration} hours</p>
                    </div>
                </div>

                <hr className="border-gray-300" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h3 className="font-semibold text-[#5D1F42] mb-1">Address</h3>
                        <p>{job.venue?.address || "—"}</p>
                    </div>
                    <div>
                        <h3 className="font-semibold text-[#5D1F42] mb-1">Contact</h3>
                        <p>
                            <strong>Client:</strong> {job.client?.name || "—"}
                        </p>
                        <p>
                            <strong>Client Tel:</strong> {job.client?.tel || "—"}
                        </p>
                        <p>
                            <strong>Venue Tel:</strong> {job.venue?.tel || "—"}
                        </p>
                    </div>
                </div>

                <hr className="border-gray-300" />

                <div>
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-[#5D1F42] mb-1">Shift Details</h3>
                        {!editingShiftDetails && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    setEditingShiftDetails(true);
                                    setShiftDetailsDraft(job.shiftDetails || "");
                                }}
                            >
                                Edit
                            </Button>
                        )}
                    </div>

                    {editingShiftDetails ? (
                        <div className="space-y-2">
                            <Textarea
                                value={shiftDetailsDraft}
                                onChange={(e) => setShiftDetailsDraft(e.target.value)}
                            />
                            <div className="flex gap-2">
                                <Button size="sm" onClick={handleShiftDetailsSave}>
                                    Save
                                </Button>
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => setEditingShiftDetails(false)}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <p className="whitespace-pre-wrap">
                            {job.shiftDetails || (
                                <span className="text-gray-500">No shift details provided</span>
                            )}
                        </p>
                    )}
                </div>

                <hr className="border-gray-300" />

                <div>
                    <h3 className="font-semibold text-[#5D1F42] mb-1">Candidate</h3>
                    {job.candidate ? (
                        <div className="space-y-2">
                            <p>
                                <strong>Name:</strong> {job.candidate.name}
                            </p>
                            <p>
                                <strong>Phone:</strong> {job.candidate.tel || "—"}
                            </p>
                            <p>
                                <strong>Offered:</strong>{" "}
                                {job.offeredAt
                                    ? new Date(job.offeredAt).toLocaleDateString("en-GB")
                                    : "Not offered"}
                            </p>
                            <p>
                                <strong>Accepted:</strong>{" "}
                                {job.acceptedAt ? (
                                    new Date(job.acceptedAt).toLocaleDateString("en-GB")
                                ) : (
                                    <Button size="sm" onClick={handleAccept}>
                                        Accept on behalf of candidate
                                    </Button>
                                )}
                            </p>
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={handleUnassign}
                            >
                                Remove Candidate
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <div className="text-yellow-800 space-y-2">
                                <p>No candidate assigned</p>
                                <Button size="sm" onClick={() => setAssignDialogOpen(true)}>
                                    Assign Candidate
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                <hr className="border-gray-300" />

                <div>
                    <h3 className="font-semibold text-[#5D1F42] mb-1">Admin Notes</h3>
                    <Textarea
                        value={notesDraft}
                        onChange={(e) => handleNotesChange(e.target.value)}
                        placeholder="Add notes..."
                    />
                </div>

                <hr className="border-gray-300" />

                <div>
                    <h3 className="font-semibold text-[#5D1F42] mb-1">Time Sheets</h3>
                    <TimesheetsTable filters={{job_id:job.id}}/>
                </div>
            </div>

            <AssignCandidateDialog
                open={assignDialogOpen}
                onClose={() => setAssignDialogOpen(false)}
                job={job}
                onAssigned={setJob}
            />
        </div>
    );
}
